import { useEffect, useMemo, useState } from "react";
import { Container, Row, Col } from "reactstrap";
import { Link, useParams } from "react-router-dom";
import { Carousel } from "react-responsive-carousel";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarDays,
  faCar,
  faCogs,
  faGasPump,
  faMapMarkerAlt,
  faPalette,
  faPhone,
  faSlidersH,
  faTag,
} from "@fortawesome/free-solid-svg-icons";
import Helmet from "../components/Helmet/Helmet";
import "../styles/car-details.css";

const normalizeBodyStyle = (value) => {
  if (typeof value === "number") {
    switch (value) {
      case 1:
        return "Sedan";
      case 2:
        return "Hatchback";
      case 3:
        return "SUV";
      case 4:
        return "Break";
      case 5:
        return "Cabrio";
      case 6:
        return "Coupe";
      default:
        return "—";
    }
  }

  if (typeof value === "string") {
    return value.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
  }

  return "—";
};

const formatValue = (value) => {
  if (typeof value === "number") {
    return value.toLocaleString("ro-RO");
  }

  return value || "—";
};

const renderOfferCard = (car, specs, className = "") => (
  <div className={`car-info-card ${className}`.trim()}>
    <div className="detail-price-row">
      <span className="section-label">Oferta curentă</span>
      <h2 className="detail-price">€{formatValue(car.price)}</h2>
    </div>

    <h3 className="detail-title">
      {car.make} {car.model}
    </h3>
    <p className="detail-subtitle">{car.title}</p>

    <div className="detail-specs">
      {specs.map((spec) => (
        <div key={spec.label} className="detail-spec-item">
          <div className="detail-spec-icon">
            <FontAwesomeIcon icon={spec.icon} />
          </div>
          <div>
            <div className="detail-spec-label">{spec.label}</div>
            <div className="detail-spec-value">{spec.value}</div>
          </div>
        </div>
      ))}
    </div>

    <div className="detail-actions">
      <a className="phone-action" href="tel:+40741383836">
        <FontAwesomeIcon icon={faPhone} />
        Suna acum
      </a>
      <Link to="/cars" className="back-action">
        Înapoi la mașini
      </Link>
    </div>

    <div className="detail-highlights">
      <div className="detail-highlight-item">
        <FontAwesomeIcon icon={faTag} />
        <span>Garanție 1 an motor + cutie</span>
      </div>
      <div className="detail-highlight-item">
        <FontAwesomeIcon icon={faCar} />
        <span>Asistență rutieră în toată țara</span>
      </div>
      <div className="detail-highlight-item">
        <FontAwesomeIcon icon={faCalendarDays} />
        <span>Verificare tehnică și livrare la domiciliu</span>
      </div>
    </div>
  </div>
);

const CarDetails = () => {
  const { id } = useParams();
  const [car, setCar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let isMounted = true;

    const fetchCar = async () => {
      setLoading(true);
      setError("");

      try {
                const response = await fetch(`https://automatrulatebackend-246f1b5aea0c.herokuapp.com/api/cars/${id}`);

        if (!response.ok) {
          throw new Error("Mașina nu a fost găsită.");
        }

        const data = await response.json();

        if (!isMounted) {
          return;
        }

        const carImages = Array.isArray(data.carImages)
          ? data.carImages
          : Array.isArray(data.images)
          ? data.images
          : Array.isArray(data.imagePaths)
          ? data.imagePaths
          : Array.isArray(data.image_paths)
          ? data.image_paths
          : [];

        const images = carImages
          .map((image) => (typeof image === "string" ? image : image?.imageUrl || image?.url || ""))
          .filter(Boolean);

        setCar({
          id: data.id ?? id,
          make: data.make || "Auto",
          model: data.model || data.title || "",
          title: data.title || `${data.make || ""} ${data.model || ""}`.trim(),
          year: data.year || "—",
          mileage: data.mileage || data.km || 0,
          price: data.price || 0,
          fuel: data.fuel || "—",
          transmission: data.transmission || "—",
          engine: data.engine || "—",
          body: normalizeBodyStyle(data.bodyStyle) || normalizeBodyStyle(data.body) || "—",
          color: data.color || "—",
          description: data.description || "",
          images,
        });
      } catch (err) {
        if (isMounted) {
          setError(err.message || "A apărut o eroare la încărcarea mașinii.");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchCar();

    return () => {
      isMounted = false;
    };
  }, [id]);

  const specs = useMemo(() => {
    if (!car) {
      return [];
    }

    return [
      { label: "An", value: car.year, icon: faCalendarDays },
      { label: "Combustibil", value: car.fuel, icon: faGasPump },
      { label: "Cutie", value: car.transmission, icon: faSlidersH },
      { label: "Motor", value: car.engine, icon: faCogs },
      { label: "Km", value: `${formatValue(car.mileage)} km`, icon: faMapMarkerAlt },
      { label: "Caroserie", value: car.body, icon: faCar },
    ];
  }, [car]);

  const descriptionLines = car?.description ? car.description.split(/\r?\n/).filter(Boolean) : [];

  if (loading) {
    return (
      <Helmet title="Se încarcă...">
        <section className="car-details-page">
          <Container>
            <div className="car-loading">Se încarcă detaliile mașinii...</div>
          </Container>
        </section>
      </Helmet>
    );
  }

  if (error || !car) {
    return (
      <Helmet title="Eroare">
        <section className="car-details-page">
          <Container>
            <div className="car-error">
              <h3>{error || "Nu s-a putut încărca această mașină."}</h3>
              <Link to="/cars" className="back-link">
                Înapoi la mașini
              </Link>
            </div>
          </Container>
        </section>
      </Helmet>
    );
  }

  return (
    <Helmet title={car.title || `${car.make} ${car.model}`}>
      <section className="car-details-page">
        <Container fluid="lg" className="car-details-shell">
          <Row className="g-4 align-items-start">
            <Col lg={8}>
              <div className="car-gallery-card">
                <Carousel
                  showThumbs
                  showStatus={false}
                  showIndicators={false}
                  showArrows
                  infiniteLoop
                  swipeable
                  autoPlay={false}
                  className="car-carousel"
                >
                  {car.images.length > 0 ? (
                    car.images.map((image, index) => (
                      <div key={`${image}-${index}`} className="car-slide">
                        <img src={image} alt={`${car.make} ${car.model}`} className="car-slide-image" />
                      </div>
                    ))
                  ) : (
                    <div className="car-slide car-slide-placeholder">
                      <span>Nu există imagini disponibile pentru această mașină.</span>
                    </div>
                  )}
                </Carousel>
              </div>

              {renderOfferCard(car, specs, "mobile-card")}

              <div className="car-description-card">
                <div className="section-label">Descriere</div>
                <h5 className="car-section-title">Despre mașina aleasă</h5>
                {descriptionLines.length > 0 ? (
                  <div className="description-text">
                    {descriptionLines.map((line, index) => (
                      <p key={`${line}-${index}`}>{line}</p>
                    ))}
                  </div>
                ) : (
                  <p className="description-text">Nu există o descriere suplimentară disponibilă pentru acest anunț.</p>
                )}
              </div>
            </Col>

            <Col lg={4}>
              {renderOfferCard(car, specs, "desktop-card")}
            </Col>
          </Row>
        </Container>
      </section>
    </Helmet>
  );
};

export default CarDetails;
